import React, { useState, useEffect } from 'react';
import { Typography, Tooltip, Menu, MenuItem, ListItemIcon, Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMFA } from '../contexts/MFAContext';
import { TabIndex } from '../navigation';
import Security2FAWarning from '../components/Security2FAWarning';
import ProfileModal from '../components/ProfileModal';

// Unified wallet context
import { useUnifiedWeb3Wallet, WalletType } from '../contexts/unified_wallet/UnifiedWeb3WalletContext';

// Import icons
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import StorageIcon from '@mui/icons-material/Storage';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

// Import ISP Market pages
import DePINHubBuckets from './depin/DePINHubBuckets';
import DePINHubBucketDetails from './depin/DePINHubBucketDetails';
import DePINHubMarketplace from './depin/DePINHubMarketplace';
import DePINHubDeals from './depin/DePINHubDeals';

interface DePINHubProps {
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
}

type ActiveSection = 'storage-units' | 'marketplace' | 'deals';

const DePINHub: React.FC<DePINHubProps> = ({ handleTabChange }) => {
  const { globalData, authState, handleSignOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { mfaState } = useMFA();
  
  // Unified wallet context
  const { 
    walletType, 
    isConnected, 
    walletAddress, 
    connectWallet, 
    disconnectWallet,
    switchWalletType 
  } = useUnifiedWeb3Wallet();
  
  // MFA state
  const is2FAEnabled = mfaState?.status?.isEnabled || false;
  
  // Profile modal state
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileModalSection, setProfileModalSection] = useState<'Profile' | 'Organization' | 'Plan' | 'Billing' | 'Wallet' | 'Referrals'>('Profile');
  
  // User menu state
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Wallet menu state
  const [walletMenuAnchor, setWalletMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Determine active section from URL
  const getActiveSection = (): ActiveSection => {
    const path = location.pathname;
    if (path.includes('/marketplace')) return 'marketplace';
    if (path.includes('/deals')) return 'deals';
    return 'storage-units';
  };
  
  const [activeSection, setActiveSection] = useState<ActiveSection>(getActiveSection());
  
  // Update active section when URL changes
  useEffect(() => {
    setActiveSection(getActiveSection());
  }, [location.pathname]);
  
  // Check if we're on a bucket details page
  const isBucketDetailsPage = location.pathname.match(/\/(stellar-buckets|sui-buckets)\/[^/]+$/);
  
  // Navigation handlers
  const handleNavigate = (section: ActiveSection) => {
    setActiveSection(section);
    switch (section) {
      case 'storage-units':
        navigate('/depin-hub/storage-units');
        break;
      case 'marketplace':
        navigate('/depin-hub/marketplace');
        break;
      case 'deals':
        navigate('/depin-hub/deals');
        break;
    }
  };
  
  const handleBackToHub = () => {
    handleTabChange({} as React.SyntheticEvent, TabIndex.Hub);
  };
  
  // Get user initials for avatar
  const getUserInitials = () => {
    const name = globalData?.userProfile?.name || globalData?.userProfile?.email || 'U';
    return name.charAt(0).toUpperCase();
  };
  
  // Format wallet address for display
  const formatWalletAddress = (address: string) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };
  
  // Handle wallet connection
  const handleConnectWallet = async (type: WalletType) => {
    setWalletMenuAnchor(null);
    switchWalletType(type);
    try {
      await connectWallet(type);
    } catch (error) {
      console.error('Failed to connect wallet:', error);
    }
  };
  
  // Sidebar items - matching main Sidebar.tsx structure
  const sidebarItems = [
    { id: 'storage-units' as ActiveSection, label: 'Storage Units', icon: <StorageIcon sx={{ fontSize: 18 }} />, tabIndex: TabIndex.StorageUnitsDePIN },
    { id: 'marketplace' as ActiveSection, label: 'Marketplace', icon: <StorefrontIcon sx={{ fontSize: 18 }} />, tabIndex: TabIndex.Marketplace },
    { id: 'deals' as ActiveSection, label: 'Deals', icon: <HandshakeIcon sx={{ fontSize: 18 }} />, tabIndex: TabIndex.MyDeals },
  ];

  return (
    <div className="w-full h-full bg-white">
      {/* Content Area - now uses common Sidebar.tsx and Header.tsx */}
      <div
        className="relative h-full text-gray-700"
        style={{ 
          backgroundColor: '#FFFFFF',
        }}
      >
        {/* Render the appropriate content based on active section */}
        {activeSection === 'storage-units' && (
          isBucketDetailsPage ? <DePINHubBucketDetails /> : <DePINHubBuckets />
        )}
        {activeSection === 'marketplace' && <DePINHubMarketplace />}
        {activeSection === 'deals' && <DePINHubDeals />}
      </div>

      {/* Profile Modal */}
      <ProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        initialSection={profileModalSection}
      />
    </div>
  );
};

export default DePINHub;
